import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Country, ICountry } from "country-state-city";
import { CountryCode, parsePhoneNumberFromString } from "libphonenumber-js";
import { useLocation, useNavigate } from "react-router-dom";
import Header from "../component/Header";
import Footer from "../component/footer";
import {
  createPaymentSession,
  submitPayment,
  validateIban,
  verifyPayment,
} from "../services/subscriptionService";
import { signup } from "../services/authService";
import { getUserDashboard } from "../services/userService";
import {
  getAuthEmail,
  getAuthToken,
  isAuthenticated,
  setAuthenticated,
  setAuthToken,
} from "../utils/authSession";
import { CheckoutPlan, getCheckoutPlan } from "../utils/checkoutSession";

const DEFAULT_PLAN: CheckoutPlan = {
  name: "Standard",
  price: "EUR59",
  priceValue: 59,
  period: "/Per Month",
  features: [
    "Access to Advanced Courses",
    "Access on any device",
    "Limited Downloads",
    "Limited Transcripts",
    "Cancel Anytime",
  ],
  summaryTitle: "Monthly Standard Subscription",
  billingDescription: "Rebills every month",
};

const COUNTRY_SEARCH_ALIASES: Record<string, string[]> = {
  GB: ["england", "scotland", "wales", "northern ireland", "uk", "britain"],
};

const IPINFO_LITE_TOKEN = "f5459ea5fbe2bb";
const IPINFO_LITE_URL = `https://api.ipinfo.io/lite/me?token=${encodeURIComponent(
  IPINFO_LITE_TOKEN
)}`;

type IpInfoLiteResponse = {
  country_code?: string;
};

const fetchIpInfoCountryCode = async () => {
  const response = await fetch(IPINFO_LITE_URL);

  if (!response.ok) {
    throw new Error("Unable to detect country from IPInfo.");
  }

  const data = (await response.json()) as IpInfoLiteResponse;
  return data.country_code?.trim().toUpperCase() || "";
};

type CheckoutLocationState = {
  plan?: CheckoutPlan;
};

const formatPrice = (price: string, priceValue: number) => {
  if (price.startsWith("$")) {
    return `${price}.00`;
  }

  if (price.startsWith("EUR")) {
    return `EUR${priceValue.toFixed(2)}`;
  }

  return price;
};

const getNextBillingDate = () => {
  const nextBilling = new Date();
  nextBilling.setMonth(nextBilling.getMonth() + 1);

  return nextBilling.toISOString().slice(0, 10);
};

const normalizePhoneCode = (phonecode: string) => {
  return phonecode.startsWith("+") ? phonecode : `+${phonecode}`;
};

const sanitizeIban = (value: string) => {
  return value.replace(/\s+/g, "").toUpperCase();
};

const validateEmailValue = (value: string) => {
  const trimmed = value.trim();

  if (!trimmed) {
    return "Email is required.";
  }

  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
    return "Enter a valid email address.";
  }

  return "";
};

const formatPhoneNumberWithPrefix = (value: string, phonePrefix: string) => {
  const trimmed = value.trim();

  if (!trimmed) {
    return `${phonePrefix} `;
  }

  if (trimmed.startsWith("+")) {
    return trimmed;
  }

  return `${phonePrefix} ${trimmed}`;
};

const getPhoneNumberWithoutPrefix = (value: string, phonePrefix: string) => {
  const trimmed = value.trim();

  if (!trimmed || !phonePrefix) {
    return trimmed;
  }

  if (trimmed.startsWith(phonePrefix)) {
    return trimmed.slice(phonePrefix.length).trimStart();
  }

  return trimmed.startsWith("+") ? trimmed.replace(/^\+\d+(?:-\d+)*\s*/, "") : trimmed;
};

const getFlagUrl = (countryCode: string) => {
  return countryCode ? `https://flagcdn.com/w20/${countryCode.toLowerCase()}.png` : "";
};

const removeTrialText = (value: string) => {
  return value.replace(/,?\s*with a 3-day free trial/gi, "").trim();
};

const validatePhoneNumberValue = (
  value: string,
  phonePrefix: string,
  countryCode: string
) => {
  const trimmed = value.trim();

  if (!trimmed) {
    return "Phone number is required.";
  }

  const digitsOnly = trimmed.replace(/\D/g, "");
  const prefixDigits = phonePrefix.replace(/\D/g, "");

  if (!trimmed.startsWith("+")) {
    return "Phone number must start with the country code.";
  }

  if (digitsOnly.length <= prefixDigits.length) {
    return "Enter your full phone number.";
  }

  const parsedPhoneNumber = parsePhoneNumberFromString(
    trimmed,
    countryCode as CountryCode
  );

  if (
    !parsedPhoneNumber ||
    parsedPhoneNumber.country !== countryCode ||
    !parsedPhoneNumber.isValid()
  ) {
    return "Enter a valid phone number for the selected country.";
  }

  return "";
};

const extractToken = (response: unknown): string | null => {
  if (!response || typeof response !== "object") {
    return null;
  }

  const payload = response as Record<string, unknown>;
  const directToken =
    payload.token ||
    payload.access_token ||
    payload.accessToken ||
    payload.authToken ||
    payload.jwt;

  if (typeof directToken === "string" && directToken.trim()) {
    return directToken;
  }

  const nestedData = payload.data;
  if (nestedData && typeof nestedData === "object") {
    const nested = nestedData as Record<string, unknown>;
    const nestedToken =
      nested.token ||
      nested.access_token ||
      nested.accessToken ||
      nested.authToken ||
      nested.jwt;

    if (typeof nestedToken === "string" && nestedToken.trim()) {
      return nestedToken;
    }
  }

  return null;
};

const extractIbanErrorMessage = (error: unknown) => {
  return error instanceof Error ? error.message : "IBAN validation failed.";
};

const Checkout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const locationState = location.state as CheckoutLocationState | null;
  const [loggedIn, setLoggedIn] = useState(isAuthenticated());
  const [email, setEmail] = useState(getAuthEmail() || "");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [streetAddress, setStreetAddress] = useState("");
  const [city, setCity] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [iban, setIban] = useState("");
  const [ibanError, setIbanError] = useState("");
  const [ibanSuccess, setIbanSuccess] = useState("");
  const [isValidatingIban, setIsValidatingIban] = useState(false);
  const [isSubmittingOrder, setIsSubmittingOrder] = useState(false);
  const [showPaymentSuccessPopup, setShowPaymentSuccessPopup] = useState(false);
  const [checkoutError, setCheckoutError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [emailTouched, setEmailTouched] = useState(false);
  const [phoneError, setPhoneError] = useState("");
  const [phoneTouched, setPhoneTouched] = useState(false);
  const [redirectCountdown, setRedirectCountdown] = useState(10);
  const lastValidatedIbanRef = useRef("");
  const phoneCountryRef = useRef<HTMLDivElement>(null);
  const countries = useMemo(() => Country.getAllCountries(), []);
  const defaultCountry = useMemo(() => {
    return countries.find((country) => country.name === "Cyprus") || countries[0];
  }, [countries]);
  const defaultCountryCode = defaultCountry?.isoCode || "CY";
  const [isDetectingCountry, setIsDetectingCountry] = useState(true);
  const [selectedCountryCode, setSelectedCountryCode] = useState(defaultCountryCode);
  const [phoneCountryCode, setPhoneCountryCode] = useState(defaultCountryCode);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [showPhoneCountryMenu, setShowPhoneCountryMenu] = useState(false);
  const [phoneCountrySearch, setPhoneCountrySearch] = useState("");

  const selectedPlan = useMemo(() => {
    const plan = locationState?.plan || getCheckoutPlan() || DEFAULT_PLAN;

    return {
      ...plan,
      billingDescription: removeTrialText(plan.billingDescription),
    };
  }, [locationState]);

  const selectedPhoneCountry = useMemo<ICountry | undefined>(
    () => countries.find((country) => country.isoCode === phoneCountryCode),
    [countries, phoneCountryCode]
  );

  const phonePrefix = useMemo(() => {
    return selectedPhoneCountry?.phonecode
      ? normalizePhoneCode(selectedPhoneCountry.phonecode)
      : "";
  }, [selectedPhoneCountry?.phonecode]);

  useEffect(() => {
    let isActive = true;

    const loadIpInfoCountry = async () => {
      try {
        const countryCode = await fetchIpInfoCountryCode();
        const detectedCountry =
          countries.find((country) => country.isoCode === countryCode) ||
          defaultCountry;

        if (!isActive) {
          return;
        }

        setSelectedCountryCode(detectedCountry?.isoCode || defaultCountryCode);
        setPhoneCountryCode(detectedCountry?.isoCode || defaultCountryCode);
      } catch {
        if (!isActive) {
          return;
        }

        setSelectedCountryCode(defaultCountryCode);
        setPhoneCountryCode(defaultCountryCode);
      } finally {
        if (isActive) {
          setIsDetectingCountry(false);
        }
      }
    };

    loadIpInfoCountry();

    return () => {
      isActive = false;
    };
  }, [countries, defaultCountry, defaultCountryCode]);

  const filteredPhoneCountries = useMemo(() => {
    const searchText = phoneCountrySearch.trim().toLowerCase();

    if (!searchText) {
      return countries;
    }

    return countries.filter((country) => {
      const searchableText = [
        country.name,
        country.isoCode,
        normalizePhoneCode(country.phonecode),
        ...(COUNTRY_SEARCH_ALIASES[country.isoCode] ?? []),
      ]
        .join(" ")
        .toLowerCase();

      return searchableText.includes(searchText);
    });
  }, [countries, phoneCountrySearch]);

  const handlePhoneCountrySelect = (isoCode: string) => {
    setPhoneCountryCode(isoCode);
    setShowPhoneCountryMenu(false);
    setPhoneCountrySearch("");
  };

  const handleSuccessRedirect = useCallback(() => {
    navigate("/my-account", { replace: true });
  }, [navigate]);

  useEffect(() => {
    if (!phonePrefix) {
      return;
    }

    setPhoneNumber((currentValue) => {
      if (!currentValue.trim()) {
        return `${phonePrefix} `;
      }

      if (currentValue.startsWith("+")) {
        const withoutOldPrefix = currentValue.replace(/^\+\d+(?:-\d+)*\s*/, "");
        return `${phonePrefix} ${withoutOldPrefix}`.trimEnd();
      }

      return `${phonePrefix} ${currentValue}`.trimEnd();
    });
  }, [phonePrefix]);

  useEffect(() => {
    if (!showPhoneCountryMenu) {
      return;
    }

    const handleDocumentClick = (event: MouseEvent) => {
      if (
        phoneCountryRef.current &&
        !phoneCountryRef.current.contains(event.target as Node)
      ) {
        setShowPhoneCountryMenu(false);
      }
    };

    document.addEventListener("mousedown", handleDocumentClick);

    return () => document.removeEventListener("mousedown", handleDocumentClick);
  }, [showPhoneCountryMenu]);

  useEffect(() => {
    if (!showPaymentSuccessPopup) {
      return;
    }

    setRedirectCountdown(10);

    const intervalId = window.setInterval(() => {
      setRedirectCountdown((currentValue) => {
        if (currentValue <= 1) {
          window.clearInterval(intervalId);
          handleSuccessRedirect();
          return 0;
        }

        return currentValue - 1;
      });
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [handleSuccessRedirect, showPaymentSuccessPopup]);

  useEffect(() => {
    setLoggedIn(isAuthenticated());
  }, []);

  useEffect(() => {
    if (!loggedIn) {
      return;
    }

    const storedEmail = getAuthEmail();
    if (storedEmail) {
      setEmail(storedEmail);
    }

    const token = getAuthToken();
    if (!token) {
      return;
    }

    const loadUserData = async () => {
      try {
        const response = await getUserDashboard(token);
        const dashboardData = response.data;

        if (dashboardData.email) {
          setEmail(dashboardData.email);
          setAuthenticated(dashboardData.email);
        }

        setFirstName(dashboardData.firstname || "");
        setLastName(dashboardData.lastname || "");
      } catch {
        // Leave checkout usable with stored session data even if dashboard fetch fails.
      }
    };

    loadUserData();
  }, [loggedIn]);

  const formattedPrice = formatPrice(selectedPlan.price, selectedPlan.priceValue);
  const nextBillingDate = getNextBillingDate();
  const fullName = `${firstName.trim()} ${lastName.trim()}`.trim();
  const visiblePhoneNumber = getPhoneNumberWithoutPrefix(phoneNumber, phonePrefix);

  useEffect(() => {
    lastValidatedIbanRef.current = "";
  }, [
    selectedPlan.name,
    selectedPlan.priceValue,
    email,
    firstName,
    lastName,
    selectedCountryCode,
    phoneCountryCode,
    streetAddress,
    city,
    postalCode,
    phoneNumber,
  ]);

  const validateCheckoutFields = () => {
    const nextEmailError = validateEmailValue(email);
    setEmailError(nextEmailError);
    if (nextEmailError) {
      throw new Error(nextEmailError);
    }

    if (!firstName.trim() || !lastName.trim()) {
      throw new Error("First name and last name are required.");
    }

    if (!loggedIn && !password.trim()) {
      throw new Error("Password is required to create your account.");
    }

    if (!selectedCountryCode.trim()) {
      throw new Error("Country is required.");
    }

    const nextPhoneError = validatePhoneNumberValue(
      phoneNumber,
      phonePrefix,
      phoneCountryCode
    );
    setPhoneError(nextPhoneError);
    if (nextPhoneError) {
      throw new Error(nextPhoneError);
    }

    if (!postalCode.trim()) {
      throw new Error("Postcode / ZIP is required.");
    }

    const sanitizedIban = sanitizeIban(iban);
    if (sanitizedIban.length < 15 || sanitizedIban.length > 34) {
      throw new Error("Enter a valid IBAN.");
    }

    if (!/^[A-Z0-9]+$/.test(sanitizedIban)) {
      throw new Error("IBAN can only contain letters and numbers.");
    }
  };

  useEffect(() => {
    if (!emailTouched) {
      return;
    }

    setEmailError(validateEmailValue(email));
  }, [email, emailTouched]);

  useEffect(() => {
    if (!phoneTouched) {
      return;
    }

    setPhoneError(validatePhoneNumberValue(phoneNumber, phonePrefix, phoneCountryCode));
  }, [phoneCountryCode, phoneNumber, phonePrefix, phoneTouched]);

  const getAuthorizedToken = async () => {
    const existingToken = getAuthToken();
    if (existingToken) {
      return existingToken;
    }

    const signupResponse = await signup({
      name: fullName,
      email: email.trim(),
      password,
      plan_id: selectedPlan.id,
    });

    const token = extractToken(signupResponse);
    if (token) {
      setAuthToken(token, email.trim());
    } else {
      setAuthenticated(email.trim());
    }

    setLoggedIn(true);
    return token;
  };

  const validateIbanWithToken = async (token: string | null, sanitizedIban: string) => {
    setIsValidatingIban(true);
    setIbanError("");
    setIbanSuccess("");

    try {
      const response = await validateIban(token, {
        iban: sanitizedIban,
      });

      const result = response.data;
      if (!result) {
        throw new Error(response.message || "IBAN validation failed.");
      }

      if (!result.valid) {
        throw new Error(result.message || "Invalid IBAN.");
      }

      lastValidatedIbanRef.current = sanitizedIban;
      const bankDetails = [result.bankName, result.bic].filter(Boolean).join(" | ");
      setIbanSuccess(
        bankDetails ? `Valid IBAN. ${bankDetails}` : "Valid IBAN."
      );
    } catch (error) {
      const message = extractIbanErrorMessage(error);
      setIbanError(message);
      throw new Error(message);
    } finally {
      setIsValidatingIban(false);
    }
  };

  const handleCompleteOrder = async () => {
    try {
      setEmailTouched(true);
      setPhoneTouched(true);
      validateCheckoutFields();
      setIsSubmittingOrder(true);
      setIbanError("");
      setCheckoutError("");

      const sanitizedIban = sanitizeIban(iban);
      await validateIbanWithToken(getAuthToken(), sanitizedIban);

      const token = await getAuthorizedToken();
      if (!token) {
        throw new Error("Signup failed. Please try again.");
      }

      const sessionResponse = await createPaymentSession(token, {
        plan_id: selectedPlan.id,
        plan_name: selectedPlan.name,
        amount: selectedPlan.priceValue,
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        email: email.trim(),
        country: selectedCountryCode.trim().toUpperCase(),
        address: streetAddress.trim() || undefined,
        city: city.trim() || undefined,
        postal_code: postalCode.trim() || undefined,
        phone: phoneNumber.trim() || undefined,
      });

      const sessionToken = sessionResponse.data?.sessionToken;
      if (!sessionToken) {
        throw new Error(sessionResponse.message || "Unable to create payment session.");
      }

      const paymentResponse = await submitPayment(token, {
        session_token: sessionToken,
        iban: sanitizedIban,
        iban_holder_name: fullName,
      });

      const paymentResult = paymentResponse.data;
      if (!paymentResult) {
        throw new Error(paymentResponse.message || "Payment submission failed.");
      }

      if (paymentResult.status !== "COMPLETED") {
        throw new Error(paymentResult.message || `Payment status: ${paymentResult.status}`);
      }

      const verifyResponse = await verifyPayment(token, {
        session_token: sessionToken,
      });

      const verifyResult = verifyResponse.data;
      if (!verifyResult) {
        throw new Error(verifyResponse.message || "Payment verification failed.");
      }

      if (verifyResult.status !== "COMPLETED") {
        throw new Error(verifyResult.message || `Payment verification status: ${verifyResult.status}`);
      }

      setShowPaymentSuccessPopup(true);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Unable to complete your order.";
      setCheckoutError(message);
      if (message.toUpperCase().includes("IBAN")) {
        setIbanError(message);
      }
    } finally {
      setIsSubmittingOrder(false);
    }
  };

  useEffect(() => {
    const sanitizedIban = sanitizeIban(iban);

    setIbanError("");
    setIbanSuccess("");

    if (!sanitizedIban) {
      return;
    }

    if (sanitizedIban.length < 15) {
      setIbanError("IBAN is too short.");
      return;
    }

    if (sanitizedIban.length > 34) {
      setIbanError("IBAN is too long.");
      return;
    }

    if (!/^[A-Z0-9]+$/.test(sanitizedIban)) {
      setIbanError("IBAN can only contain letters and numbers.");
      return;
    }

    lastValidatedIbanRef.current = "";
  }, [
    iban,
  ]);

  return (
    <div>
      <Header />

      <section className="main">
        <div className="container checkout-page-wrap">
          <div className="checkout-panel-card">
            <div className="details-card">
              <h2 className="font-bold checkout-title">
                Complete Payment for {selectedPlan.name}
              </h2>
              <p className="checkout-note">
                Server-to-server checkout: validate your IBAN and payment will
                be submitted automatically.
              </p>
              <h2 className="font-bold checkout-title">Your Information</h2>
              <div className="billing-details-fields">
                <div className={`form-row ${loggedIn ? "single-field" : ""}`}>
                  <div className="form-group full">
                    <label>
                      Email <span>*</span>
                    </label>
                    <input
                      type="email"
                      placeholder="john.doe@example.com"
                      value={email}
                      onChange={(event) => {
                        setEmailTouched(true);
                        setEmail(event.target.value);
                      }}
                      onBlur={() => {
                        setEmailTouched(true);
                        setEmailError(validateEmailValue(email));
                      }}
                      readOnly={loggedIn}
                    />
                    {emailTouched && emailError && (
                      <p className="mt-2 text-sm text-red-500">{emailError}</p>
                    )}
                  </div>
                  {!loggedIn && (
                    <div className="form-group full">
                      <label>
                        Password <span>*</span>
                      </label>
                      <span className="password-input">
                        <input
                          type="password"
                          placeholder="Password"
                          name="account_password"
                          id="account_password"
                          value={password}
                          onChange={(event) => setPassword(event.target.value)}
                        />
                        <button
                          type="button"
                          className="show-password-input"
                          aria-label="Show password"
                          aria-describedby="account_password"
                        />
                      </span>
                    </div>
                  )}
                </div>
                <div className="form-row">
                  <div className="form-group full">
                    <label>
                      First name <span>*</span>
                    </label>
                    <input
                      type="text"
                      placeholder="John"
                      value={firstName}
                      onChange={(event) => setFirstName(event.target.value)}
                    />
                  </div>
                  <div className="form-group full">
                    <label>
                      Last name <span>*</span>
                    </label>
                    <input
                      type="text"
                      placeholder="Doe"
                      value={lastName}
                      onChange={(event) => setLastName(event.target.value)}
                    />
                  </div>
                </div>
                <div className="form-row single-field">
                  <div className="form-group full phone-number-group">
                    <label>
                      Phone <span>*</span>
                    </label>
                    <div className="phone-input-row">
                      <div
                        className="phone-country-select notranslate"
                        ref={phoneCountryRef}
                        translate="no"
                      >
                        <button
                          type="button"
                          className="phone-country-trigger"
                          onClick={() =>
                            setShowPhoneCountryMenu((currentValue) => !currentValue)
                          }
                          disabled={isDetectingCountry}
                          aria-haspopup="listbox"
                          aria-expanded={showPhoneCountryMenu}
                          aria-label="Phone country code"
                        >
                          {phoneCountryCode && (
                            <img
                              src={getFlagUrl(phoneCountryCode)}
                              alt=""
                              aria-hidden="true"
                              width="24"
                              height="18"
                            />
                          )}
                          <span translate="no">{phonePrefix}</span>
                        </button>
                        {showPhoneCountryMenu && (
                          <div className="phone-country-menu">
                            <input
                              type="search"
                              className="phone-country-search"
                              placeholder="Search country or code"
                              value={phoneCountrySearch}
                              onChange={(event) => setPhoneCountrySearch(event.target.value)}
                              autoFocus
                            />
                            <div className="phone-country-options" role="listbox">
                              {filteredPhoneCountries.map((country) => {
                                const isSelected = country.isoCode === phoneCountryCode;

                                return (
                                  <button
                                    type="button"
                                    className={`phone-country-option ${
                                      isSelected ? "selected" : ""
                                    }`}
                                    key={country.isoCode}
                                    onClick={() => handlePhoneCountrySelect(country.isoCode)}
                                    role="option"
                                    aria-selected={isSelected}
                                  >
                                    <img
                                      src={getFlagUrl(country.isoCode)}
                                      alt=""
                                      aria-hidden="true"
                                      width="24"
                                      height="18"
                                    />
                                    <span className="phone-country-name">
                                      {country.name}
                                    </span>
                                    <span className="phone-country-code" translate="no">
                                      {normalizePhoneCode(country.phonecode)}
                                    </span>
                                  </button>
                                );
                              })}
                              {filteredPhoneCountries.length === 0 && (
                                <div className="phone-country-empty">
                                  No countries found
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                      <input
                        type="tel"
                        placeholder={
                          phonePrefix
                            ? "70 123 45 67"
                            : "Detecting country code..."
                        }
                        value={visiblePhoneNumber}
                        disabled={isDetectingCountry || !phonePrefix}
                        onChange={(event) => {
                          setPhoneTouched(true);
                          setPhoneNumber(formatPhoneNumberWithPrefix(event.target.value, phonePrefix));
                        }}
                        onBlur={() => {
                          setPhoneTouched(true);
                          const nextPhoneNumber = formatPhoneNumberWithPrefix(phoneNumber, phonePrefix);
                          setPhoneNumber(nextPhoneNumber);
                          setPhoneError(
                            validatePhoneNumberValue(
                              nextPhoneNumber,
                              phonePrefix,
                              phoneCountryCode
                            )
                          );
                        }}
                      />
                    </div>
                    {phoneTouched && phoneError && (
                      <p className="mt-2 text-sm text-red-500">{phoneError}</p>
                    )}
                  </div>
                </div>
              </div>
              <h2 className="font-bold checkout-title">Your Billing Details</h2>
              <div className="billing-details-fields">
                <div className="form-row">
                  <div className="form-group full">
                    <label>
                      Street address <span>*</span>
                    </label>
                    <input
                      type="text"
                      value={streetAddress}
                      onChange={(event) => setStreetAddress(event.target.value)}
                    />
                  </div>
                  <div className="form-group full">
                    <label>
                      Town / City <span>*</span>
                    </label>
                    <input
                      type="text"
                      value={city}
                      onChange={(event) => setCity(event.target.value)}
                    />
                  </div>
                </div>
                <div className="form-row bottom-row">
                  <div className="form-group postcode">
                    <label>
                      Postcode / ZIP <span>*</span>
                    </label>
                    <input
                      type="text"
                      value={postalCode}
                      onChange={(event) => setPostalCode(event.target.value)}
                    />
                  </div>
                  <div className="form-group country">
                    <label>
                      Country <span>*</span>
                    </label>
                    <div className="select-wrap">
                      <select
                        className="custom-select h-[46px]"
                        value={selectedCountryCode}
                        disabled={isDetectingCountry}
                        onChange={(event) => setSelectedCountryCode(event.target.value)}
                      >
                        {!selectedCountryCode && (
                          <option value="">Detecting country...</option>
                        )}
                        {countries.map((country) => (
                          <option key={country.isoCode} value={country.isoCode}>
                            {country.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              </div>
              <h2 className="font-bold checkout-title">
                SEPA Direct Debit Information
              </h2>
              <div className="payment-box">
                <label className="payment-header">
                  <input type="radio" name="payment" id="sepaRadio" defaultChecked />
                  <div className="custom-radio" />
                  <span className="payment-title">SEPA Direct Debit</span>
                  <img src=".\sepa.svg" alt="SEPA" width="67" height="24" />
                </label>
                <div className="payment-content">
                  <p className="checkout-note">
                    Mandate Information.
                    <br /> By providing your IBAN and confirming this payment,
                    you are authorizing us to send instructions to your bank to
                    debit your account and your bank to debit your account in
                    accordance with those instructions. You are entitled to a
                    refund from your bank under the terms and conditions of your
                    agreement with your bank. A refund must be claimed within 8
                    weeks starting from the date on which your account was
                    debited.
                  </p>
                  <div className="checkout-fields">
                    <label>
                      IBAN <span>*</span>
                    </label>
                    <input
                      type="text"
                      placeholder="IBAN (e.g. DE89370400440532013000)"
                      autoComplete="off"
                      className="checkout-input"
                      value={iban}
                      onChange={(event) => setIban(event.target.value)}
                    />
                    {isValidatingIban && (
                      <p className="mt-2 text-sm text-[#4b5563]">
                        Validating IBAN...
                      </p>
                    )}
                    {!isValidatingIban && ibanError && (
                      <p className="mt-2 text-sm text-red-500">{ibanError}</p>
                    )}
                    {!isValidatingIban && !ibanError && ibanSuccess && (
                      <p className="mt-2 text-sm text-[#113A28]">{ibanSuccess}</p>
                    )}
                  </div>
                  <h2 className="font-bold checkout-title">
                    Payment Information
                  </h2>
                  <p className="mb-0 checkout-note">
                    All transactions are secure and encrypted. No information is ever stored on our servers.
                  </p>
                </div>
              </div>
              <p className="mt-3 text-[#4b5563] text-center">
                Your personal data will be used to process your order, support
                your experience throughout this website, and for other purposes
                described in our privacy policy .
              </p>
              <div className="place-btn-wrapper">
                {checkoutError && (
                  <p className="mb-3 text-sm text-center text-red-500">{checkoutError}</p>
                )}
                <button
                  type="button"
                  className="font-semibold place-order bg-[#4ce470] text-[#113a28] px-8 py-4 rounded-[999px] font-bold text-sm hover:bg-[#3bc45d] transition-colors shadow-lg shadow-gray-300"
                  name="place_order"
                  id="place_order"
                  value="Complete Your Order Now"
                  data-value="Complete Your Order Now"
                  disabled={isSubmittingOrder || isDetectingCountry}
                  onClick={handleCompleteOrder}
                >
                  {isSubmittingOrder ? (
                    <span className="checkout-button-spinner" aria-label="Processing" />
                  ) : (
                    "Complete Your Order Now"
                  )}
                </button>
              </div>
              <div className="sepa-icon">
                <img
                  src=".\SEPA Logo PNG Vector (SVG) Free Download.jpg"
                  alt="SEPA"
                  width="240"
                  height="150"
                />
              </div>
              <div className="checkout-features">
                <div className="feature-item">
                  <div className="feature-title">
                    <img src=".\time.svg" alt="Time icon" width="32" height="32" />
                  </div>
                  <div className="feature-text text-[#4b5563]">
                    <strong>Cancel Anytime</strong>
                    Cancel anytime, see T&amp;Cs for more info
                  </div>
                </div>
                <div className="feature-item">
                  <div className="feature-title">
                    <img src=".\lock.svg" alt="Lock icon" width="32" height="32" />
                  </div>
                  <div className="feature-text text-[#4b5563]">
                    <strong>Secure Processing</strong>
                    Encrypted, secure order processing
                  </div>
                </div>
              </div>
              {/* <p className="text-center checkout-note">
                We use GoCardless to process your Direct Debit payments. More
                information on how GoCardless processes your personal data and
                your data protection rights, including your right to object, is
                available at{" "}
                <a
                  href="https://gocardless.com/legal/privacy/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  gocardless.com/legal/privacy/
                </a>
              </p>
              <div className="guarantee-box">
                <div className="logo-wrap">
                  <img src=".\direct debit.png" alt="Direct Debit Logo" width="220" height="80" />
                </div>
                <div className="guarantee-title">Direct Debit Guarantee</div>
              </div> */}
            </div>
            <div className="subscription-card">
              <h2>Subscription Information</h2>
              <div className="subscription-item">
                <div className="product-image">
                  <img src=".\favicon.ico" alt="product" width="48" height="48" />
                </div>
                <div className="product-info">
                  <div className="top-row">
                    <h3>{selectedPlan.summaryTitle}</h3>
                    <span className="price">{formattedPrice}</span>
                  </div>
                  <p>{selectedPlan.billingDescription}</p>
                  <div className="font-light billing-info text-[#4ce470] text-[14px]">
                    <span>Next Billing:</span>
                    <span>{nextBillingDate}</span>
                  </div>
                </div>
              </div>

              <div className="divider" />
              <div className="summary-row">
                <span>Subtotal</span>
                <span>{formattedPrice}</span>
              </div>
              <div className="divider" />
              <div className="total-row">
                <span>Total</span>
                <span>{formattedPrice}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {showPaymentSuccessPopup && (
        <div className="checkout-popup-overlay" role="dialog" aria-modal="true">
          <div className="checkout-popup">
            <p>
              Your payment request has been received. The amount will be deducted
              from your IBAN within 2-3 business days.
            </p>
            <p className="checkout-popup-countdown">
              Redirecting you in {redirectCountdown}
            </p>
            <button
              type="button"
              onClick={handleSuccessRedirect}
            >
              OK
            </button>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default Checkout;
